import React, { createContext, useContext, useReducer, ReactNode, Dispatch } from 'react';
import dayjs from 'dayjs';

export interface Ad {
    adName: string;
    primaryText: string;
    descriptionText: string;
    headline: string;
    mediaType: 'image' | 'video';
    mediaUrl: string;
    videoId: string;
    thumbnailUrl: string;
    imageSource: 'meta' | 's3';
    metaImageHash: string;
    metaThumbnailImageHash: string;
    prefilledMessage: string;
    welcomeMessage: string;
}

// --- State Interface ---
export interface CampaignState {
    isEditing: boolean;
    // Campaign Details
    campaignName: string;
    selectedObjective: string;
    specialAdCategories: string[];
    spendCap?: number;

    // Ad Account & Org
    selectedAdAccountId: string;
    selectedWaba: string | null;
    selectedFbPageId: string;
    orgId: string;
    selectedPlatforms: string[];
    instagramAccountId: string | null;

    // Ad Set Details
    adSetName: string;
    budgetType: 'DAILY' | 'LIFETIME';
    dailyBudget?: number;
    lifetimeBudget?: number;
    startDate: dayjs.Dayjs | null;
    endDate: dayjs.Dayjs | null;
    selectedGender: string;
    ageRange: [number, number];
    adsetSchedule: any[];

    // Audience
    useCustomAudiences: boolean;
    customAdSetId: string | null;
    customAdSetName: string | null;
    advCustomAudience: boolean;
    setBeneficiary: boolean;
    beneficiary: string | null;
    selectLocations: any[];
    excludedLocations: any[];
    selectedTargeting: any[];

    // Ad Creative (Multiple Ads)
    ads: Ad[];
    currentAdIndex: number;

    // UI State
    currentStep: number;
    isSubmitting: boolean;
    estimatedRange: number | undefined;
    advantagePlus: boolean;
}

// --- Initial State ---
const initialAd: Ad = {
    adName: '',
    primaryText: '',
    descriptionText: '',
    headline: '',
    mediaType: 'image',
    mediaUrl: '',
    videoId: '',
    thumbnailUrl: '',
    imageSource: 's3',
    metaImageHash: '',
    metaThumbnailImageHash: '',
    prefilledMessage: '',
    welcomeMessage: ''
};

export const initialState: CampaignState = {
    isEditing: false,
    campaignName: '',
    selectedObjective: 'OUTCOME_ENGAGEMENT',
    specialAdCategories: ['NONE'],
    spendCap: undefined,
    selectedAdAccountId: '',
    selectedWaba: null,
    selectedFbPageId: '',
    orgId: '',
    selectedPlatforms: ['facebook', 'instagram'],
    instagramAccountId: null,
    adSetName: '',
    budgetType: 'DAILY',
    dailyBudget: undefined,
    lifetimeBudget: undefined,
    startDate: null,
    endDate: null,
    selectedGender: 'all',
    ageRange: [18, 65],
    adsetSchedule: [],
    useCustomAudiences: false,
    customAdSetId: null,
    customAdSetName: null,
    advCustomAudience: false,
    setBeneficiary: false,
    beneficiary: null,
    selectLocations: [],
    excludedLocations: [],
    selectedTargeting: [],
    ads: [{ ...initialAd }],
    currentAdIndex: 0,
    currentStep: 0,
    isSubmitting: false,
    estimatedRange: undefined,
    advantagePlus: false
};

// --- Actions ---
export type CampaignAction =
    | { type: 'SET_FIELD'; field: keyof CampaignState; value: any }
    | {
        type: 'SET_MULTIPLE_FIELDS'; payload: Partial<CampaignState & {
            // Legacy fields for backward compatibility/ease of use in initialization
            adName?: string;
            primaryText?: string;
            descriptionText?: string;
            headline?: string;
            mediaUrl?: string;
            prefilledMessage?: string;
            welcomeMessage?: string;
        }>
    }
    | { type: 'RESET_FORM' }
    | { type: 'UPDATE_AD_FIELD'; field: keyof Ad; value: any }
    | { type: 'ADD_AD' }
    | { type: 'REMOVE_AD'; index: number }
    | { type: 'SET_CURRENT_AD_INDEX'; index: number };

// --- Reducer ---
const campaignReducer = (state: CampaignState, action: CampaignAction): CampaignState => {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        case 'SET_MULTIPLE_FIELDS': {
            // Check if any legacy ad fields are present in the payload
            const payload = action.payload;
            return { ...state, ...payload };
        }
        case 'RESET_FORM':
            return initialState;

        case 'UPDATE_AD_FIELD': {
            const updatedAds = [...state.ads];
            updatedAds[state.currentAdIndex] = {
                ...updatedAds[state.currentAdIndex],
                [action.field]: action.value
            };
            return { ...state, ads: updatedAds };
        }

        case 'ADD_AD':
            if (state.ads.length == 3) return state;
            return {
                ...state,
                ads: [...state.ads, { ...initialAd }],
                currentAdIndex: state.ads.length // Select the new ad
            };

        case 'REMOVE_AD': {
            if (state.ads.length <= 1) return state; // Don't allow removing if only 1 ad

            const newAds = state.ads.filter((_, idx) => idx !== action.index);
            let newIndex = state.currentAdIndex;

            // Adjust index if we removed the current or a previous ad
            if (state.currentAdIndex >= action.index && state.currentAdIndex > 0) {
                newIndex = state.currentAdIndex - 1;
            }

            return { ...state, ads: newAds, currentAdIndex: newIndex };
        }

        case 'SET_CURRENT_AD_INDEX':
            return { ...state, currentAdIndex: action.index };
        default:
            return state;
    }
};

// --- Context ---
interface CampaignContextType {
    state: CampaignState;
    dispatch: Dispatch<CampaignAction>;
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

// --- Provider ---
export const CampaignProvider: React.FC<{ children: ReactNode; initialValues?: Partial<CampaignState> }> = ({ children, initialValues }) => {
    const [state, dispatch] = useReducer(campaignReducer, { ...initialState, ...initialValues });

    return (
        <CampaignContext.Provider value={{ state, dispatch }}>
            {children}
        </CampaignContext.Provider>
    );
};

// --- Hook ---
export const useCampaign = () => {
    const context = useContext(CampaignContext);
    if (!context) {
        throw new Error('useCampaign must be used within a CampaignProvider');
    }
    return context;
};
